// src/utils/useAppInstallation.js
import i18n from '@/libs/i18n'
import { getPlatform } from '@/utils/platform'
import { storage } from '@/utils/storage'

// eslint-disable-next-line import/prefer-default-export
export const useAppInstallation = () => {
  // Stable snapshot for the whole session
  const platformInfo = getPlatform()

  const PERSIST_KEY = 'web-view-prompt'
  const SESSION_KEY = 'otj_app_open_attempted'
  const PACKAGE_NAME = 'app.onetimejob'
  const APP_SCHEME = 'onetimejob'

  /**
   * Normalize a target url for safe passing into intent / Play referrer.
   * - Forces https
   * - Unifies allowed hosts to onetimejob.io (keeps your original behavior)
   */
  const normalizeTargetUrl = raw => {
    try {
      const u = new URL(raw || window.location.href)

      // Force https to avoid intent / referrer issues
      u.protocol = 'https:'

      // Unify host (your existing behavior)
      const host = (u.hostname || '').toLowerCase()
      if (
        host === 'www.onetimejob.io'
        || host === 'onetimejob.app'
        || host === 'www.onetimejob.app'
        || host === 'www.onetimejob.es'
        || host === 'onetimejob.es'
      ) {
        u.hostname = 'onetimejob.io'
      }

      return u.toString()
    } catch (_) {
      return raw || (typeof window !== 'undefined' ? window.location.href : '')
    }
  }

  /**
   * Used when native app supports onetimejob://open/<path>?<query>
   */
  const convertToAppScheme = targetUrl => {
    try {
      const url = new URL(normalizeTargetUrl(targetUrl))
      const appPath = url.pathname || '/'
      return `${APP_SCHEME}://open${appPath}${url.search || ''}`
    } catch (_) {
      return `${APP_SCHEME}://open/`
    }
  }

  const buildPlayUrlWithTarget = (packageName, targetUrl) => {
    const play = new URL('https://play.google.com/store/apps/details')
    play.searchParams.set('id', packageName)
    play.searchParams.set('hl', i18n.locale)

    if (targetUrl) {
      const ref = new URLSearchParams()
      ref.set('target', targetUrl)
      play.searchParams.set('referrer', ref.toString())
    } else {
      play.searchParams.delete('referrer')
    }

    return play.toString()
  }

  const googlePlayLinkWithTarget = (targetUrl = window.location.href) =>
    buildPlayUrlWithTarget(PACKAGE_NAME, normalizeTargetUrl(targetUrl))

  /**
   * Checks if the native app is installed using getInstalledRelatedApps (when supported).
   * Returns:
   *  - true/false when supported
   *  - null when unsupported or inconclusive
   */
  const checkIsInstalled = async () => {
    try {
      if (typeof navigator === 'undefined') return null
      if (typeof navigator.getInstalledRelatedApps !== 'function') return null

      const apps = await navigator.getInstalledRelatedApps()
      if (!Array.isArray(apps)) return null

      // Try matching by id/url/platform where possible
      return apps.some(app => {
        const id = String(app?.id || '')
        const url = String(app?.url || '')
        const platform = String(app?.platform || '')

        // Most common matching signals
        if (id === PACKAGE_NAME) return true
        if (id.includes(PACKAGE_NAME)) return true
        if (url.includes(PACKAGE_NAME)) return true

        // Some browsers may report Play-related identifiers
        if (platform === 'play' && (id.includes(PACKAGE_NAME) || url.includes(PACKAGE_NAME))) return true

        return false
      })
    } catch (_) {
      return null
    }
  }

  /**
   * Intent URL that opens installed app; if not installed -> browser fallback opens Play page.
   */
  const buildIntentUrl = (targetUrl = window.location.href) => {
    const normalized = normalizeTargetUrl(targetUrl)
    const fallback = googlePlayLinkWithTarget(normalized)

    try {
      const u = new URL(normalized)
      const host = 'open'
      const path = u.pathname || '/'
      const query = u.search || ''

      return `intent://${host}${path}${query}#Intent;scheme=${APP_SCHEME};package=${PACKAGE_NAME};S.browser_fallback_url=${encodeURIComponent(
        fallback,
      )};end`
    } catch (_) {
      return fallback
    }
  }

  const markAsDismissed = () => {
    try { storage.setItem(PERSIST_KEY, '1') } catch (_) {}
  }

  const hasBeenDismissed = () => storage.getItem(PERSIST_KEY) === '1'

  const hasAttemptedThisSession = () => {
    try { return sessionStorage.getItem(SESSION_KEY) === '1' } catch (_) { return false }
  }

  /**
   * Try to open native app (Android browser only).
   * - Never runs in WebView / PWA-standalone
   * - Single attempt per session
   * - Uses visibility/blur heuristic to decide if app switch happened
   */
  const openAppOrStore = async (targetUrl = window.location.href, { delayMs = 900 } = {}) => {
    // Only Android browser should attempt.
    if (!platformInfo.isAndroidBrowser) return { didAttempt: false }

    // Hard guard: never attempt inside WebView (including old app builds).
    if (platformInfo.isWebView || platformInfo.isAndroidWebView) return { didAttempt: false }

    // Hard guard: do not bounce when running as standalone PWA.
    if (platformInfo.isPwaStandalone) return { didAttempt: false }

    // If document is already hidden, visibility detection is unreliable; skip.
    const visibilityBefore = document.visibilityState
    if (visibilityBefore === 'hidden') return { didAttempt: false }

    // Single attempt per session.
    try {
      if (sessionStorage.getItem(SESSION_KEY) === '1') return { didAttempt: false }
      sessionStorage.setItem(SESSION_KEY, '1')
    } catch (_) {}

    const normalized = normalizeTargetUrl(targetUrl)
    const timeoutMs = Math.max(300, Number(delayMs) || 900)

    // Attempt via intent URL
    try {
      window.location.href = buildIntentUrl(normalized)
    } catch (_) {
      // If intent navigation fails, go to Play (future behavior)
      // For now: keep this as a best-effort fallback.
      try { window.location.href = googlePlayLinkWithTarget(normalized) } catch (_) {}
      return { didAttempt: true, opened: false }
    }

    const opened = await new Promise(resolve => {
      let done = false

      const cleanup = () => {
        document.removeEventListener('visibilitychange', onVis, true)
        window.removeEventListener('blur', onBlur, true)
      }

      const finish = val => {
        if (done) return
        done = true
        cleanup()
        resolve(val)
      }

      const onVis = () => {
        if (document.visibilityState === 'hidden') finish(true)
      }

      const onBlur = () => finish(true)

      document.addEventListener('visibilitychange', onVis, true)
      window.addEventListener('blur', onBlur, true)

      window.setTimeout(() => {
        const stillVisible = document.visibilityState === 'visible' && visibilityBefore === 'visible'
        finish(!stillVisible)
      }, timeoutMs)
    })

    if (opened) return { didAttempt: true, opened: true }

    // FUTURE: if not opened -> redirect to Play.
    // Currently you can enable it by uncommenting:
    // try { window.location.href = googlePlayLinkWithTarget(normalized) } catch (_) {}
    // return { didAttempt: true, opened: false }

    return { didAttempt: true, opened: false }
  }

  return {
    platform: platformInfo,
    googlePlayLinkWithTarget,
    checkIsInstalled,
    buildIntentUrl,
    convertToAppScheme,
    openAppOrStore,
    markAsDismissed,
    hasBeenDismissed,
    hasAttemptedThisSession,
  }
}
